import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatKronor, type PlanComputation } from './milestonePlan';
import type { AssignmentValues } from './useContractWizardDraft';
import type { ContractType } from '../../types';

interface WizardSummaryProps {
  /** Fixed price (Anbud/Fast pris) or ÄTA; default fixed. */
  contractType?: ContractType;
  values: AssignmentValues;
  attachmentCount: number;
  /** Omitted until the user has reached step 2. */
  pricing?: { plan: PlanComputation; stageCount: number };
}

/** Live summary next to steps 1–2. */
export function WizardSummary({ contractType = 'fixed', values, attachmentCount, pricing }: WizardSummaryProps) {
  const { t } = useTranslation();
  const missing = <span className="text-muted fst-italic">{t('contracts.wizard.summary.missing')}</span>;
  const kr = t('contracts.wizard.pricing.unitKr');
  const allocatedPercent = pricing && pricing.plan.afterRot > 0
    ? Math.round((pricing.plan.allocated / pricing.plan.afterRot) * 100)
    : 0;

  return (
    <div className="contract-wizard-summary-sections">
      <section className="contract-wizard-summary-section">
        <h3 className="contract-wizard-summary-heading">{t('contracts.wizard.steps.assignment.title')}</h3>
        <dl className="contract-wizard-summary-list">
          <dt>{t('contracts.preview.contractTypeLabel')}</dt>
          <dd>{t(`contracts.types.${contractType}`)}</dd>
          <dt>{t('contracts.descriptions.contractNameTitle')}</dt>
          <dd>{values.contractName.trim() || missing}</dd>
          <dt>{t('contracts.wizard.summary.attachments')}</dt>
          <dd>{t('contracts.wizard.files.count', { count: attachmentCount })}</dd>
        </dl>
      </section>
      <section className="contract-wizard-summary-section">
        <h3 className="contract-wizard-summary-heading">{t('contracts.wizard.steps.pricing.title')}</h3>
        {pricing ? (
          <dl className="contract-wizard-summary-list">
            <dt>{t('contracts.wizard.pricing.totalLabel')}</dt>
            <dd>{formatKronor(pricing.plan.gross)} {kr}</dd>
            <dt>{t('contracts.wizard.pricing.plannedRotLabel')}</dt>
            <dd>{formatKronor(pricing.plan.rot)} {kr}</dd>
            <dt>{t('contracts.wizard.pricing.afterRotLabel')}</dt>
            <dd className="fw-semibold">{formatKronor(pricing.plan.afterRot)} {kr}</dd>
            <dt>{t('contracts.wizard.summary.stages')}</dt>
            <dd>{t('contracts.wizard.summary.stageCount', { count: pricing.stageCount })}</dd>
            <dt>{t('contracts.wizard.pricing.allocated')}</dt>
            <dd>{t('contracts.wizard.summary.allocatedPercent', { percent: allocatedPercent })}</dd>
          </dl>
        ) : (
          <p className="small text-muted mb-0">{t('contracts.wizard.summary.pricingPending')}</p>
        )}
      </section>
    </div>
  );
}

export default WizardSummary;
